import _extends from '@babel/runtime/helpers/esm/extends';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import { useState, useRef, useMemo, useEffect } from 'react';
import { IconButton } from '@paypalcorp/pp-react-icon-button';
import { CloseIcon } from '@paypalcorp/pp-react-icons';
import { devWarning, utilStyles, ZINDEX_MODAL, uniqueId } from '@paypalcorp/pp-react-utils';
import { css, useTheme, jsx } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { math } from 'polished';
import { textStyles } from '@paypalcorp/pp-react-text';
var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }
  var sysColorBackgroundSurface = tokens.sysColorBackgroundSurface,
    sysSizeIconLg = tokens.sysSizeIconLg,
    sysSizeIconMd = tokens.sysSizeIconMd,
    sysSpaceLg = tokens.sysSpaceLg,
    sysSpaceSm = tokens.sysSpaceSm,
    sysSpaceXl = tokens.sysSpaceXl;
  var _utilStyles = utilStyles(tokens),
    elevationStyles = _utilStyles.elevationStyles;
  var _textStyles = textStyles(tokens),
    textBody = _textStyles.body;
  var headerWidth = '44rem';
  var headerFlexBasis = math("2 * ".concat(sysSpaceXl));
  var base = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: focus_flow_base;\n    position: fixed;\n    inset: 0 0 0 0;\n    background-color: ", ";\n    z-index: ", ";\n  "])), sysColorBackgroundSurface, ZINDEX_MODAL);
  var wrapper = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: focus_flow_wrapper;\n    display: flex;\n    flex-direction: column;\n    align-items: center;\n    position: absolute;\n    width: 100%;\n  "])));
  var container = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: focus_flow_container;\n    height: 100%;\n    margin: 0 auto;\n  "])));
  var content = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: focus_flow_content;\n    overflow: auto;\n    height: 90vh;\n    position: relative;\n    border-radius: inherit;\n    ", ";\n    padding: ", " 0;\n    margin: 0 auto;\n    width: ", ";\n\n    @media screen and (max-width: ", ") {\n      width: 100%;\n      padding: ", ";\n      height: 84vh;\n    }\n  "])), textBody(), sysSpaceLg, '36rem', headerWidth, sysSpaceLg);
  var header = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: focus_flow_header;\n    width: 100%;\n  "])));
  var headerContent = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: focus_flow_header_content;\n    display: flex;\n    justify-content: space-between;\n    align-items: center;\n    flex-wrap: wrap;\n    gap: ", ";\n    position: relative;\n    flex: 0 0 auto;\n    z-index: 1;\n    width: ", ";\n    margin: 0 auto;\n    padding: ", " 0;\n\n    @media screen and (max-width: ", ") {\n      width: 100%;\n      padding: ", " ", ";\n    }\n  "])), sysSpaceLg, headerWidth, sysSpaceSm, headerWidth, sysSpaceSm, sysSpaceLg);
  var headerAction = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: focus_flow_header_action;\n    flex: 0 0 ", ";\n  "])), sysSizeIconLg);
  var headerTitle = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: focus_flow_header_title;\n    flex: 1 1 ", ";\n    margin: ", " 0;\n    text-align: center;\n    ", ";\n  "])), headerFlexBasis, sysSpaceSm, textBody());
  var headerClose = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: focus_flow_header_close;\n    flex: 0 0 ", ";\n  "])), sysSizeIconLg);
  var headerLogo = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: focus_flow_header_logo;\n    display: flex;\n    flex: 1 1 ", ";\n    justify-content: center;\n    margin-top: ", ";\n    width: ", ";\n    height: ", ";\n  "])), headerFlexBasis, sysSpaceSm, sysSizeIconMd, sysSizeIconMd);
  var headerScrolled = css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    label: header_scrolled;\n    ", "\n  "])), elevationStyles.level3);
  return {
    base: base,
    content: content,
    container: container,
    header: header,
    headerContent: headerContent,
    headerAction: headerAction,
    headerClose: headerClose,
    headerLogo: headerLogo,
    headerTitle: headerTitle,
    headerScrolled: headerScrolled,
    wrapper: wrapper
  };
}
var _excluded = ["action", "children", "className", "closeButtonLabel", "closeButtonProps", "containerClassName", "mainClassName", "contentContainerRef", "headerClassName", "id", "onClose", "showLogo", "title"];
var FocusFlow = function (_ref) {
  var action = _ref.action,
    children = _ref.children,
    className = _ref.className,
    _ref$closeButtonLabel = _ref.closeButtonLabel,
    closeButtonLabel = _ref$closeButtonLabel === void 0 ? 'close' : _ref$closeButtonLabel,
    _ref$closeButtonProps = _ref.closeButtonProps,
    closeButtonProps = _ref$closeButtonProps === void 0 ? {} : _ref$closeButtonProps,
    containerClassName = _ref.containerClassName,
    mainClassName = _ref.mainClassName,
    contentContainerRef = _ref.contentContainerRef,
    headerClassName = _ref.headerClassName,
    id = _ref.id,
    onClose = _ref.onClose,
    showLogo = _ref.showLogo,
    title = _ref.title,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    headerScroll = _useState2[0],
    setHeaderScroll = _useState2[1];
  var closeButtonRef = useRef();
  var refContent = useRef();
  var contentRef = contentContainerRef || refContent;
  var actionZoneRef = useRef();
  var tokens = useTheme();
  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var idBase = id !== null && id !== void 0 ? id : uniqueId();
  var titleId = "header_".concat(idBase);
  var handleClick = function (evt) {
    if (onClose) {
      onClose(evt);
    }
  };
  /* istanbul ignore next */

  var handleScroll = function () {
    if (contentRef && contentRef.current) {
      var contents = contentRef.current;
      var newHeaderScroll = contents.scrollTop !== 0;
      if (newHeaderScroll !== headerScroll) {
        setHeaderScroll(newHeaderScroll);
      }
    }
  }; // Put focus on fist focusable item
  // istanbul ignore next

  var handleFocus = function () {
    var focused = false; // For Forms

    if (!focused && contentRef && contentRef.current) {
      //@ts-ignore
      var focusableEls = contentRef.current.querySelectorAll('a, button, textarea, input, select');
      if (focusableEls.length > 0) {
        focusableEls[0].focus();
        focused = true;
      }
    } // Otherwise focus on action item or close button

    if (!focused) {
      if (action && actionZoneRef && actionZoneRef.current) {
        //@ts-ignore
        var focusableActionEl = actionZoneRef.current.querySelectorAll('a, button, select');
        if (focusableActionEl.length > 0) {
          focusableActionEl[0].focus();
        }
      } else {
        var closeButton = closeButtonRef.current;
        if (closeButton) {
          //@ts-ignore
          closeButton.focus();
        }
      }
    }
  }; // check scroll and put focus

  useEffect(function () {
    handleScroll();
    handleFocus();
  }, []);
  var titleContentCss = [showLogo ? _styles.headerLogo : _styles.headerTitle];
  var closeButtonTag = jsx(IconButton, _extends({
    svgIconComponent: CloseIcon,
    onClick: function onClick(e) {
      return handleClick(e);
    },
    buttonType: "tertiary"
  }, closeButtonProps, {
    ref: closeButtonRef,
    "data-ppui": "true"
  }), closeButtonLabel);
  var titleContentZone = jsx("div", {
    css: titleContentCss,
    id: titleId,
    "data-ppui": "true"
  }, function titleContents() {
    if (showLogo) {
      return jsx("img", {
        src: "https://www.paypalobjects.com/paypal-ui/logos/svg/paypal-mark-color.svg",
        alt: "paypal-logo",
        "data-ppui": "true"
      });
    }
    return title;
  }());
  var closeButtonZone = jsx("div", {
    css: [_styles.headerClose],
    "data-ppui": "true"
  }, closeButtonTag);
  var actionZone = action ? jsx("div", {
    css: [_styles.headerAction],
    ref: actionZoneRef,
    "data-ppui": "true"
  }, action) : jsx("div", {
    css: [_styles.headerAction],
    "data-ppui": "true"
  });
  var header = jsx("header", {
    className: headerClassName,
    css: [_styles.header, /* istanbul ignore next */
    headerScroll && _styles.headerScrolled],
    "data-ppui": "true"
  }, jsx("div", {
    css: [_styles.headerContent],
    "data-ppui": "true"
  }, actionZone, titleContentZone, closeButtonZone));
  return jsx("main", {
    css: _styles.base,
    className: mainClassName,
    id: id,
    "data-testid": "focusflow",
    "data-ppui-info": "focus-flow_2.3.5",
    "aria-live": "polite",
    "aria-label": showLogo ? title : ''
  }, jsx("article", _extends({
    css: [_styles.container],
    className: containerClassName
  }, rest, {
    "aria-hidden": "false",
    "data-ppui": "true"
  }), header, jsx("section", {
    css: _styles.content,
    className: className,
    onScroll: handleScroll,
    ref: contentRef,
    "data-ppui": "true"
  }, children)));
};
export { FocusFlow, styles as focusFlowStyles };